/** From this many reviews the count itself is proof; below it the count makes a business look small. */
const REVIEW_COUNT_SHOWN_FROM = 30;

/** Ratings are shown to one decimal, the way Google shows them. */
const RATING_STEP = 10;

/**
 * A star rating as people read it: one decimal, with a whole number shown bare.
 * @param {number} rating The average rating, 1 to 5.
 * @returns {string} "4.7", or "5" for 5.0.
 */
const formatStarRating = (rating: number): string => {
  const rounded = Math.round(rating * RATING_STEP) / RATING_STEP;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

/**
 * Whether a review count is high enough to show.
 * @param {number | null | undefined} count How many reviews.
 * @returns {boolean} True from REVIEW_COUNT_SHOWN_FROM up.
 */
const showsReviewCount = (count: number | null | undefined): boolean => (count ?? 0) >= REVIEW_COUNT_SHOWN_FROM;

/**
 * The line a site uses to show its reviews: the count once it is impressive ("120+ Reviews"),
 * otherwise the rating ("4.7-Star Rated").
 * @param {{ rating?: number | null; count?: number | null }} reviews The business's rating and review count.
 * @returns {string | null} The label, or null when there is neither a big count nor a rating.
 */
const reviewProofLabel = ({ rating, count }: { rating?: number | null; count?: number | null }): string | null => {
  if (showsReviewCount(count)) {
    return `${count}+ Reviews`;
  }
  if (typeof rating === "number" && rating > 0) {
    return `${formatStarRating(rating)}-Star Rated`;
  }
  return null;
};

export { REVIEW_COUNT_SHOWN_FROM, formatStarRating, reviewProofLabel, showsReviewCount };
